import {
  CashRegisterFilters,
  EventGateFilters,
  FilterEventType,
  MyEventsFilter,
  Paginated,
  ProEventVisitorsFilter,
  Sortable,
} from "@ac-expo/api-types";

export type EventsFilter = FilterEventType & Sortable<Paginated>;

export const eventsQueryKeys = {
  all: ["events"] as const,
  lists: () => [...eventsQueryKeys.all, "list"] as const,
  list: (filters?: FilterEventType) => [...eventsQueryKeys.lists(), { filters }] as const,
  map: (filters?: FilterEventType) => [...eventsQueryKeys.all, "map", { filters }] as const,
  me: (filters?: MyEventsFilter) => [...eventsQueryKeys.all, "me", { filters }] as const,
  meInfinite: (filters?: MyEventsFilter) =>
    [...eventsQueryKeys.all, "me", "infinite", { filters }] as const,
  get: (id: string) => [...eventsQueryKeys.all, "get", id] as const,
  // Public event fetched with a guest session Bearer token (embed): the token is part of
  // the key so a Group unlock refetches, while `get(id)` invalidations still match.
  getAsGuest: (id: string, accessToken: string) =>
    [...eventsQueryKeys.get(id), "guest", accessToken] as const,
  getBySlug: (slug: string) => [...eventsQueryKeys.all, "getBySlug", slug] as const,
  getComplete: (id: string) => [...eventsQueryKeys.get(id), "complete"] as const,
  autocomplete: (searchText: string) =>
    [...eventsQueryKeys.all, "autocomplete", searchText] as const,
  exhibitTo: (params: Paginated<FilterEventType>) =>
    [...eventsQueryKeys.all, "exhibit-to", params] as const,
  cashRegisters: (id: string) => [...eventsQueryKeys.get(id), "cash-registers"] as const,
  cashRegistersWithParams: (id: string, params?: CashRegisterFilters) =>
    [...eventsQueryKeys.cashRegisters(id), params] as const,
  gates: (id: string, params?: EventGateFilters) =>
    [...eventsQueryKeys.get(id), "gates", params] as const,
  entries: (id: string) => [...eventsQueryKeys.get(id), "entries"] as const,
  proVisitors: (eventId: string) => [...eventsQueryKeys.get(eventId), "pro-visitors"] as const,
  proVisitorsWithParams: (eventId: string, params?: ProEventVisitorsFilter) =>
    [...eventsQueryKeys.proVisitors(eventId), params] as const,
};

export const acExpoEventsQueryKeys = {
  all: ["events-acexpo"] as const,
  list: (params?: Paginated<FilterEventType | object>) =>
    [...acExpoEventsQueryKeys.all, "list", params ?? {}] as const,
  reported: (params: Paginated<FilterEventType | object>) =>
    [...acExpoEventsQueryKeys.all, "list", "reported", params] as const,
  suggestions: (search: string, organiserId?: string) =>
    [...acExpoEventsQueryKeys.all, "suggestions", search, organiserId] as const,
};

export const likeEventsQueryKeys = {
  likes: ["likes"] as const,
  ids: () => [...likeEventsQueryKeys.likes, "id"] as const,
  summaries: () => [...likeEventsQueryKeys.likes, "summary"] as const,
  summariesCard: () => [...likeEventsQueryKeys.likes, "summary", "card"] as const,
};

export const visitedEventsQueryKeys = {
  visited: ["visited"] as const,
  summaries: () => [...visitedEventsQueryKeys.visited, "summary"] as const,
  summariesCard: () => [...visitedEventsQueryKeys.visited, "summary", "card"] as const,
};

export const cashRegisterHoldersQueryKeys = {
  all: ["holders"] as const,
  byEvent: (eventId: string) => [...cashRegisterHoldersQueryKeys.all, eventId],
  byEventAndId: (eventId: string, holderId: string) => [
    ...cashRegisterHoldersQueryKeys.byEvent(eventId),
    holderId,
  ],
};

export const cashRegisterSessionsQueryKeys = {
  all: ["sessions"] as const,
  byEvent: (eventId: string) => [...cashRegisterSessionsQueryKeys.all, eventId],
  byEventAndDate: (eventId: string, date: string) => [
    ...cashRegisterSessionsQueryKeys.byEvent(eventId),
    date,
  ],
  byEventAndDateAndCashRegister: (eventId: string, date: string, cashRegisterId: string) => [
    ...cashRegisterSessionsQueryKeys.byEventAndDate(eventId, date),
    cashRegisterId,
  ],
  byEventAndId: (eventId: string, sessionId: string) => [
    ...cashRegisterSessionsQueryKeys.byEvent(eventId),
    sessionId,
  ],
};

export default eventsQueryKeys;
